import { Box, Button, FormHelperText, InputLabel, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Form, Formik, FormikProps, FormikHelpers } from "formik";
import { addActionPlanResumedFormValidationSchema } from "validations/schemas";
import dayjs from "dayjs";
import moment from "moment";
import RCAUsersDropdown from "pages/Corporate/RCA/components/RCAUsersDropdown";
import { useRCAMainStyles } from "pages/Corporate/RCA/RCA.styles";
import { useStyles } from "../../ActionPlansTable.styles";
import { IAddActionPlanFormValues } from "../../ActionPlansTableInterfaces";
import useDateFormat from "hooks/useDateFormat";
import { useTranslation } from "react-i18next";
import { NAME_MAX_INPUT_RCA_ACTION_PLAN } from "constants/constants";

interface IActionPlanInlineFormProps {
    isVisible: boolean;
    isCreating: boolean;
    millId?: number | null;
    formRef: React.RefObject<FormikProps<IAddActionPlanFormValues>>;
    actionPlanNameInputRef: React.RefObject<HTMLInputElement>;
    onSubmit: (
        values: IAddActionPlanFormValues,
        formikHelpers: FormikHelpers<IAddActionPlanFormValues>,
    ) => Promise<void>;
}

const ActionPlanInlineForm = ({
    isVisible,
    isCreating,
    millId,
    formRef,
    actionPlanNameInputRef,
    onSubmit,
}: IActionPlanInlineFormProps) => {
    const mainClasses = useRCAMainStyles();
    const classes = useStyles();
    const { t } = useTranslation();
    const { getDateFormatByLanguage } = useDateFormat();

    if (!isVisible) {
        return null;
    }

    return (
        <Formik
            innerRef={formRef}
            initialValues={{
                actionPlanName: "",
                dueDate: moment(new Date()).add(4, "days").toDate(),
                assignedToId: "",
            }}
            validationSchema={addActionPlanResumedFormValidationSchema}
            onSubmit={onSubmit}
        >
            {({ values, setFieldValue, errors, touched, handleChange, handleSubmit }) => (
                <Form>
                    <div className={classes.inlineFormRow}>
                        <Box className={classes.inlineFormField}>
                            <InputLabel id="outlined-actionPlanName-label">
                                <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                    {t("rca.rca_library.rca_detail.action_plans.summary")}
                                </Typography>
                            </InputLabel>
                            <TextField
                                inputRef={actionPlanNameInputRef}
                                autoFocus={isVisible}
                                disabled={isCreating}
                                aria-labelledby="outlined-actionPlanName-label"
                                fullWidth
                                id="outlined-actionPlanName-input"
                                className={classes.contentFormTextField}
                                name="actionPlanName"
                                type="text"
                                variant="outlined"
                                value={values.actionPlanName}
                                onChange={handleChange}
                                error={touched.actionPlanName && Boolean(errors.actionPlanName)}
                                helperText={
                                    touched.actionPlanName && errors.actionPlanName
                                        ? t(errors.actionPlanName)
                                        : `${values.actionPlanName.length}/${NAME_MAX_INPUT_RCA_ACTION_PLAN}`
                                }
                                inputProps={{ maxLength: NAME_MAX_INPUT_RCA_ACTION_PLAN }}
                                FormHelperTextProps={{
                                    sx: {
                                        ...(touched.actionPlanName && errors.actionPlanName
                                            ? {}
                                            : { textAlign: "right" }),
                                        margin: 0,
                                    },
                                }}
                            />
                        </Box>
                        <Box className={classes.inlineFormField}>
                            <InputLabel>
                                <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                    {t("rca.issues.issue_detail.due_date")}
                                </Typography>
                            </InputLabel>
                            <DatePicker
                                className={`${classes.inlineFormDatePicker}`}
                                value={dayjs(values.dueDate)}
                                disablePast
                                format={getDateFormatByLanguage()}
                                onChange={(date) => {
                                    setFieldValue("dueDate", date);
                                }}
                                disabled={isCreating}
                            />
                        </Box>
                        <Box className={classes.inlineFormField}>
                            <InputLabel id="outlined-assignedToId-label">
                                <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                    {t("rca.issues.issue_detail.owner")}
                                </Typography>
                            </InputLabel>
                            <RCAUsersDropdown
                                fullWidth
                                millId={String(millId) || ""}
                                multiple={false}
                                value={values.assignedToId}
                                onChange={(value) => {
                                    setFieldValue("assignedToId", value);
                                }}
                                placeholder={t("rca.settings.owners_dropdown.select_owner")}
                                disabled={isCreating}
                            />
                            {!!errors.assignedToId && !!touched.assignedToId && (
                                <FormHelperText
                                    style={{ position: "absolute" }}
                                    className={mainClasses.contentFormHelperText}
                                >
                                    {t(errors.assignedToId)}
                                </FormHelperText>
                            )}
                        </Box>
                        <Button
                            className={mainClasses.contentButton}
                            variant="contained"
                            color="info"
                            onClick={() => {
                                handleSubmit();
                            }}
                            disabled={isCreating}
                        >
                            {t("rca.action_plans.add_action_plan_modal.done")}
                        </Button>
                    </div>
                </Form>
            )}
        </Formik>
    );
};

export default ActionPlanInlineForm;
